import SportradarService from '../../../services/sportradar';

/* eslint-disable no-shadow */
const state = () => ({
  rawSchema: {},
  sport: null,
  seasons: {},
  Season: {},
  teams: [],
  Team: {},
  players: [],
  Player: {},
  games: [],
  Game: {},
  conferenceFilter: 'all',
});

/* eslint-disable no-param-reassign */
const mutations = {
  SET_SEASONS(state, payload) {
    state.seasons = payload;
  },
  SET_SCHEMA(state, payload) {
    state.rawSchema = payload;
  },
  SET_SPORT(state, value) {
    state.sport = value;
  },
  SET_SEASON(state, value) {
    state.Season = value;
  },
  SET_TEAMS(state, value) {
    state.teams = value;
  },
  SET_TEAM(state, value) {
    state.Team = value;
  },
  SET_PLAYER(state, value) {
    state.Player = value;
  },
  SET_PLAYERS(state, value) {
    state.players = value;
  },
  SET_GAME(state, value) {
    state.Game = value;
  },
  SET_GAMES(state, value) {
    state.games = value;
  },
  SET_CONFERENCE_FILTER(state, value) {
    state.conferenceFilter = value || 'all';
  },
};
/* eslint-enable no-param-reassign */

/**
 * Maps GraphQL schema fields from an introspection query response.
 * This function processes the given introspection query response to filter and map the schema types
 * and their respective fields. It excludes certain types and system types (starting with '__').
 * @param {Object} introspectionResponse - The response object from a GraphQL introspection query.
 * @returns {Object} An object mapping each type name to its fields, with field types.
 */
function mapGqlSchemaFields(introspectionResponse) {
  const EXCLUDED_TYPE_NAMES = ['Query', 'Endpoints', 'RecordValue'];
  const { types } = introspectionResponse.__schema; // eslint-disable-line no-underscore-dangle
  const filteredTypes = types.filter((type) => {
    const { kind, name } = type;
    return kind === 'OBJECT' && !name.startsWith('__') && !EXCLUDED_TYPE_NAMES.includes(name);
  });
  const schemaTypesList = filteredTypes.map((t) => t.name);

  return filteredTypes.reduce((typeHash, currentType) => {
    const { name: typeName, fields } = currentType;

    const typeFields = {};

    fields.forEach((field) => {
      const { name: fieldName } = field;
      const { name: ofTypeName } = field?.type?.ofType || {};
      typeFields[fieldName] = (schemaTypesList.includes(ofTypeName)) ? 'Object' : ofTypeName;
    });

    typeHash[typeName] = typeFields; // eslint-disable-line no-param-reassign

    return typeHash;
  }, {});
}


// Helper functions

/**
 * Removes the '__typename' field from an object
 * @param {Object} item - The object from which to remove the '__typename' field.
 * @returns {Object} The new object without the '__typename' field.
 */
function removeTypename(item) {
  const { __typename, ...rest } = item;
  return rest;
}

/**
 * Transforms an array of team objects into an object with teamId as the key for each team.
 *
 * @param {Object[]} teams - An array of team objects.
 * @returns {Object} An object with teamId as keys and team objects as values.
 */
function transformTeams(teams) {
  return teams.reduce((acc, team) => {
    acc[team.teamId] = removeTypename(team);
    return acc;
  }, {});
}

/**
 * Normalizes an array of game objects
 * removes the '__typename' field,
 * converts the 'scheduledFor' field from a string or number to a Date object,
 *  and sorts the array based on the 'scheduledFor' dates in ascending order.
 *
 * @param {Object[]} games - An array of game objects.
 * @returns {Object[]} A new array of game objects that have been normalized.
 */
function normalizeGames(games) {
  return games.map(game => ({
    ...removeTypename(game),
    scheduledFor: new Date(Number(game.scheduledFor)),
  })).sort((a, b) => a.scheduledFor - b.scheduledFor);
}

/**
 * Transforms an array of season objects by filtering out any season that is scheduled and then 
 * restructuring the array into an object with seasonId as the key for each season.
 * removes the '__typename' field,
 *
 * @param {Object[]} seasons - An array of season objects.
 * @returns {Object} An object with seasonId as keys and season objects as values, excluding any seasons that are scheduled.
 */
function transformSeasons(seasons) {
  return seasons?.filter(season => season.seasonStatus !== 'scheduled')
    .reduce((acc, season) => {
      acc[season.seasonId] = removeTypename(season);
      return acc;
    }, {});
}

const actions = {
  async setSeason({ commit }, season) {
    commit('SET_SEASON', season);
  },
  async setTeam({ commit }, team) {
    commit('SET_TEAM', team);
  },
  async setPlayer({ commit }, player) {
    commit('SET_PLAYER', player);
  },
  async setGame({ commit }, game) {
    commit('SET_GAME', game);
  },
  async setConferenceFilter({ commit }, conferenceFilter) {
    commit('SET_CONFERENCE_FILTER', conferenceFilter);
  },
  async syncDataFromElement({ commit, rootState }, payload) {
    const { sport, controlOptions, seasonId, teamId, playerId, gameId, conferenceFilter } = payload;
    const schemaRes = await SportradarService.getSchema(sport);

    const schema = mapGqlSchemaFields(schemaRes, sport);

    commit('SET_SPORT', sport);
    commit('SET_SCHEMA', schema);
    commit('SET_CONFERENCE_FILTER', conferenceFilter);
    
    const statType = controlOptions.player ? 'player' : 'team';
    const statPeriod = controlOptions.game ? 'game' : 'season';

    let res;
    const ownerId = rootState.production?.production?.owner;
    try {
     res = await SportradarService.getStatSelectionData({
        seasonId,
        teamId,
        playerId,
        gameId,
        leagueName: sport,
        ownerId,
        statPeriod,
        statType,
      });
    } catch (error) {
      console.error('Error fetching sportradar data:', error);
      return {};
    }

        const transformedSeasons = transformSeasons(res.allSeasons);
        const alphabetizedTeams = transformTeams(res.teamsForSeason);

        if (statType === 'player') {
          commit('SET_PLAYERS', res.currentPlayers);
          const currentPlayer = res.currentPlayer || res.currentPlayers.find((p) => p.playerId === playerId) || res.currentPlayers[0] || {};
          if (currentPlayer) {
            commit('SET_PLAYER', removeTypename(currentPlayer));
          }
        }

        if (statPeriod === 'game') {
          const normalizedGames = normalizeGames(res.currentGames);
          commit('SET_GAMES', normalizedGames);
          const currentGame = res.currentGames.find((g) => g.gameId === gameId) || res.currentGame || res.currentGames[0] || {};
          if (currentGame) {
            commit('SET_GAME', removeTypename(currentGame));
          }
        }

        commit('SET_SEASON', removeTypename(res.currentSeason));
        commit('SET_TEAM', removeTypename(res.currentTeam));
        commit('SET_SEASONS', transformedSeasons);
        commit('SET_TEAMS', alphabetizedTeams);

        return res;

  },
  async statSelectorChange({ commit, state, rootState }, { seasonId, teamId, statPeriod, statType, leagueName, playerId, selectionType }) {

    const ownerId = rootState.production?.production?.owner;
    const requestParams = {
      leagueName,
      seasonId,
      teamId,
      playerId,
      statPeriod,
      statType,
      ownerId,
    };

    let res;
    if (selectionType === 'season') {
      res = await SportradarService.statSelectorSeasonChange(requestParams);
    }

    if (selectionType === 'team') {
      res = await SportradarService.statSelectorTeamChange(requestParams);
    }

    if (res.teamsForSeason?.length) {
      const alphabetizedTeams = transformTeams(res.teamsForSeason);
      commit('SET_TEAMS', alphabetizedTeams);
      commit('SET_SEASON', state.seasons[seasonId]);
    }

    if (statType === 'player') {
      commit('SET_PLAYERS', res?.currentPlayers);
      const currentPlayer = res?.currentPlayer || res?.currentPlayers[0] || {};
      if (currentPlayer) {
        commit('SET_PLAYER', currentPlayer);
      }
    }

    if (statPeriod === 'game') {
      const normalizedGames = normalizeGames(res.currentGames);
      commit('SET_GAMES', normalizedGames);
      const currentGame =  res?.currentGame || res?.currentGames[0] || {};
      if (currentGame) {
        commit('SET_GAME', currentGame);
      }
    }

    return res;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
